/**
 * Componente: Editor de un diseño
 * modo="crear": formulario simple (nombre + colores) para el primer guardado
 * de un diseño nuevo — todavía no tiene id, así que no hay canvas ni logo
 * (el logo pasa a ser un elemento del canvas, se agrega recién en modo
 * "editar", una vez que el diseño ya existe).
 * modo="editar": pantalla única y continua — arriba nombre/colores/config
 * de impresión, debajo el canvas de posiciones (Frente/Dorso, agregar
 * texto/imagen, arrastrar/redimensionar). "Guardar" persiste todo junto
 * sin sacar al usuario; "✓ Listo, volver a mis diseños" es la única acción
 * que efectivamente navega de vuelta a la galería (guarda antes de salir).
 * El autoguardado de borrador corre en paralelo mientras se trabaja.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import LicenseCard from './LicenseCard';
import ConfiguracionImpresionModal from './ConfiguracionImpresionModal';
import AvisoBorradorModal from './AvisoBorradorModal';
import { formatearFecha, formatearCuil } from '../utils/fecha';
import { useClasesMunicipio, clasesDeEjemplo } from '../utils/clasesLicencia';
import {
  FORMATO_CODIGOS,
  FORMATO_CODIGO_DESCRIPCION,
  formatoDeElemento,
  resolverClasesCodigoDescripcion,
  pxDisenoACqw,
  PUNTOS_MINIMOS_IMPRESION,
  interDisponible,
  textoClasesSoloCodigos,
  useTipografiasListas,
} from '../utils/formatoClases';
import {
  STROKE_DEGRADE,
  IconConfiguracion,
  IconAdvertencia,
  IconExito,
  IconAgregar,
  IconImagen,
  IconLinea,
  IconRectangulo,
  IconCirculo,
  IconQR,
  IconMunicipio,
  IconPersona,
} from './icons';
import SelectorColor from './SelectorColor';
import PanelPosicionTamano from './PanelPosicionTamano';
import {
  ANCHO_TARJETA_MM,
  ALTO_TARJETA_MM,
  dimensionesTarjetaMm,
  ratioTarjeta,
  aplicarMedidaALista,
  aplicarFoto34ALista,
} from '../utils/posicionTamano';
import '../styles/padron.css';
import '../styles/disenador.css';

const API_URL = process.env.REACT_APP_API_URL;

// Íconos de botón chico con trazo en degradé de marca (mismo criterio que
// Padron.jsx / Personal.jsx / Auditoria.jsx).
const ICONO_BOTON = { size: 14, stroke: STROKE_DEGRADE };

// Espera de inactividad antes de autoguardar el borrador, para no mandar un
// request por cada tecla o cada pixel arrastrado.
const DEBOUNCE_BORRADOR_MS = 2500;
// Cuánto queda a la vista el "Borrador guardado" antes de desaparecer solo.
const DURACION_INDICADOR_OK_MS = 2000;
// Cuánto queda a la vista el "✓ Guardado" del botón Guardar antes de volver
// a su estado normal (a diferencia de "Listo", este botón no navega).
const DURACION_CONFIRMACION_MS = 1200;

const CONTRIBUYENTE_EJEMPLO = {
  apellido: 'PÉREZ',
  nombre: 'JUAN CARLOS',
  tipo_documento: 'DNI',
  numero_documento: '30123456',
  // Crudo (11 dígitos) — el formato XX-XXXXXXXX-X lo aplica formatearCuil()
  // en resolverValorLayout / resolverTextoPreview, igual que en la impresión.
  cuit_cuil: '20301234563',
  fecha_nacimiento: '1985-06-15',
  nacionalidad: 'ARGENTINA',
  sexo: 'M',
  domicilio_calle: 'SAN MARTÍN',
  domicilio_numero: '1234',
  domicilio_localidad: 'CORRIENTES',
  domicilio_provincia: 'CORRIENTES',
  domicilio_cp: '3400',
  telefono: '3794123456',
  email: 'juan.perez@ejemplo.com',
  fecha_emision: '2026-01-15',
  fecha_vencimiento: '2031-01-15',
  clases: ['B1', 'B2'],
  grupo_sanguineo: 'O+',
  donante_organos: true,
  usa_lentes: false,
  observaciones_medicas: 'Ninguna',
  tipo_tramite: 'nueva',
  numero_licencia: '30123456',
  numero_tramite: '2026000123',
  foto_url: null,
};

// Cada opción es un campo REAL de la tabla contribuyentes (o una
// combinación práctica de varios, como "nombre" o "domicilio"). No hay
// campos de la tabla que se hayan dejado afuera a propósito, salvo los que
// no tiene sentido "atar" a un texto del canvas: claves internas
// (id/municipio_id), timestamps de auditoría, el estado del registro
// (activo/inactivo, no es un dato de la licencia) y foto_url/firma_url
// (son imágenes, van por la herramienta "Agregar imagen", no por acá).
const FUENTES_DATO = [
  { value: 'texto_fijo', label: 'Texto fijo' },
  { value: 'nombre', label: 'Nombre y apellido' },
  { value: 'apellido', label: 'Apellido (solo)' },
  { value: 'nombre_solo', label: 'Nombre (solo)' },
  { value: 'dni', label: 'DNI' },
  { value: 'numero_documento', label: 'Número de documento (solo)' },
  { value: 'cuit_cuil', label: 'CUIT/CUIL' },
  { value: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
  { value: 'nacionalidad', label: 'Nacionalidad' },
  { value: 'sexo', label: 'Sexo' },
  { value: 'domicilio', label: 'Domicilio (completo)' },
  { value: 'domicilio_calle_numero', label: 'Domicilio (calle y número)' },
  { value: 'domicilio_localidad', label: 'Localidad' },
  { value: 'domicilio_provincia', label: 'Provincia' },
  { value: 'domicilio_cp', label: 'Código postal' },
  { value: 'telefono', label: 'Teléfono' },
  { value: 'email', label: 'Email' },
  { value: 'vencimiento', label: 'Vencimiento' },
  { value: 'emision', label: 'Emisión' },
  { value: 'clases', label: 'Clases' },
  { value: 'grupo_sanguineo', label: 'Grupo sanguíneo' },
  { value: 'donante_organos', label: 'Donante de órganos' },
  { value: 'usa_lentes', label: 'Usa lentes' },
  { value: 'observaciones_medicas', label: 'Observaciones médicas' },
  { value: 'tipo_tramite', label: 'Tipo de trámite' },
  { value: 'numero_licencia', label: 'Número de licencia' },
  { value: 'numero_tramite', label: 'Número de trámite' },
];

// Valores de ejemplo para previsualizar cómo se ve cada fuente de dato,
// mismo contribuyente ficticio que ya se usa en el resto del Diseñador.
const VALORES_EJEMPLO = {
  nombre: 'PÉREZ, JUAN CARLOS',
  apellido: 'PÉREZ',
  nombre_solo: 'JUAN CARLOS',
  dni: '30.123.456',
  numero_documento: '30.123.456',
  // Crudo — resolverTextoPreview le aplica formatearCuil() al vuelo, así el
  // Diseñador y la impresión muestran exactamente el mismo formato.
  cuit_cuil: '20301234563',
  fecha_nacimiento: '15/06/1985',
  nacionalidad: 'ARGENTINA',
  sexo: 'M',
  domicilio: 'SAN MARTÍN 1234, CORRIENTES, CORRIENTES',
  domicilio_calle_numero: 'SAN MARTÍN 1234',
  domicilio_localidad: 'CORRIENTES',
  domicilio_provincia: 'CORRIENTES',
  domicilio_cp: '3400',
  telefono: '3794123456',
  email: 'juan.perez@ejemplo.com',
  vencimiento: '15/01/2031',
  emision: '15/01/2026',
  clases: 'B1, B2',
  grupo_sanguineo: 'O+',
  donante_organos: 'Sí',
  usa_lentes: 'No',
  observaciones_medicas: 'Ninguna',
  tipo_tramite: 'Nueva',
  numero_licencia: '30.123.456',
  numero_tramite: '2026000123',
};

const MIN_SIZE_PCT = 5;
const clamp = (valor, min, max) => Math.min(Math.max(valor, min), max);

// Ancho de diseño del lienzo (coincide con .canvas-lienzo max-width y con
// CANVAS_ANCHO_DISENO_PX de LicenseCard). Los valores en px que el usuario
// configura (tamaño de fuente, grosores, radio) se calibran contra este
// ancho y se renderizan en "cqw" para que escalen con el lienzo real —
// idéntico a como LicenseCard escala la tarjeta. .canvas-lienzo declara
// container-type: inline-size para que el cqw se resuelva contra su ancho.
const CANVAS_ANCHO_DISENO_PX = 700;
const pxACqw = (px) => `${((Number(px) || 0) / CANVAS_ANCHO_DISENO_PX) * 100}cqw`;

// Medidas reales de la tarjeta ISO/IEC 7810 ID-1 (mm): única fuente en utils/posicionTamano.js (la comparten el
// ratio del QR, el paso del movimiento con flechas y el panel "Posición y tamaño"). No repetir estos literales
// sueltos en otro lado.

// Proporción real de la tarjeta. width/height de un elemento se guardan como
// % del ancho/alto del lienzo respectivamente (dos ejes distintos, no la
// misma unidad), así que un QR con width%==height% NO se ve cuadrado en
// pantalla: para que se vea cuadrado de verdad hay que mantener
// height = width * RATIO_TARJETA. Se usa tanto para el resize forzado del
// QR como para su tamaño por defecto al agregarlo.
const RATIO_TARJETA = ratioTarjeta();

// Paso del movimiento con flechas del teclado, expresado en mm sobre la
// tarjeta real (con Shift, PASO_MM_SHIFT). Se convierte a % de cada eje con
// las medidas de arriba — mismo criterio que el arrastre con mouse, que
// también trabaja en % y clampea a [0, 100 - tamaño].
const PASO_MM = 1;
const PASO_MM_SHIFT = 5;

// contenido_tipo como catálogo de opciones (no un booleano ni un switch
// suelto): agregar una fuente nueva en el futuro (ej. "link_verificacion")
// es sumar una entrada acá y un resolver en RESOLVERES_CONTENIDO_QR, sin
// tocar el resto del mecanismo (ni el tipo de dato, ni el drag/resize, ni
// el guardado).
const OPCIONES_CONTENIDO_QR = [
  { value: 'datos_plano', label: 'Datos del contribuyente (texto plano)' },
];

// Cada resolver recibe el contribuyente (real o el de ejemplo) y devuelve
// el string a codificar en el QR.
const RESOLVERES_CONTENIDO_QR = {
  datos_plano: (contribuyente) =>
    `DNI:${contribuyente.numero_documento}|NOMBRE:${contribuyente.apellido} ${contribuyente.nombre}|VENC:${formatearFecha(contribuyente.fecha_vencimiento)}`,
};

const resolverContenidoQR = (elemento, contribuyente) => {
  const resolver = RESOLVERES_CONTENIDO_QR[elemento.contenido_tipo];
  return resolver ? resolver(contribuyente) : '';
};

// Contraste WCAG (luminancia relativa) entre el color del QR y el fondo:
// un QR con poco contraste puede no escanear, aunque el color en sí sea
// legible para un ojo humano. 3:1 es más laxo que el mínimo de WCAG para
// texto (4.5:1) a propósito — accesibilidad de texto y "lo puede leer un
// lector de códigos" son cosas distintas, y exigir 4.5 rechazaría
// combinaciones que en la práctica escanean bien.
const UMBRAL_CONTRASTE_QR = 3;

const hexALuminancia = (hex) => {
  const canal = (valor) => {
    const c = valor / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const r = canal(parseInt(hex.slice(1, 3), 16));
  const g = canal(parseInt(hex.slice(3, 5), 16));
  const b = canal(parseInt(hex.slice(5, 7), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const calcularContraste = (hex1, hex2) => {
  const l1 = hexALuminancia(hex1);
  const l2 = hexALuminancia(hex2);
  const [claro, oscuro] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (claro + 0.05) / (oscuro + 0.05);
};

let contadorId = 0;
const generarId = () => {
  contadorId += 1;
  return `el-${Date.now()}-${contadorId}`;
};

const crearElementoTextoDefault = () => ({
  id: generarId(),
  tipo: 'texto',
  x: 30,
  y: 42,
  width: 40,
  height: 16,
  rotacion: 0,
  fuente_dato: 'texto_fijo',
  texto_fijo: 'Texto',
  tamano_fuente: 12,
  alineacion: 'izquierda',
  color: '#2c3e50',
  negrita: false,
  cursiva: false,
});

const crearElementoImagenDefault = (fuenteImagen) => ({
  id: generarId(),
  tipo: 'imagen',
  x: 35,
  y: 35,
  width: 30,
  height: 30,
  rotacion: 0,
  fuente_imagen: fuenteImagen,
});

const crearElementoLineaDefault = () => ({
  id: generarId(),
  tipo: 'linea',
  x: 10,
  y: 50,
  width: 40,
  height: 4,
  rotacion: 0,
  color: '#2c3e50',
  grosor: 2,
});

const crearElementoRectanguloDefault = () => ({
  id: generarId(),
  tipo: 'rectangulo',
  x: 30,
  y: 30,
  width: 35,
  height: 20,
  rotacion: 0,
  color_relleno: '#1a9b8e',
  color_borde: null,
  grosor_borde: 2,
  radio_esquina: 0,
});

const crearElementoCirculoDefault = () => ({
  id: generarId(),
  tipo: 'circulo',
  x: 40,
  y: 40,
  width: 20,
  height: 20,
  rotacion: 0,
  color_relleno: '#1a9b8e',
  color_borde: null,
  grosor_borde: 2,
});

const crearElementoQRDefault = () => ({
  id: generarId(),
  tipo: 'qr',
  x: 40,
  y: 35,
  width: 20,
  height: 20 * RATIO_TARJETA,
  rotacion: 0,
  contenido_tipo: 'datos_plano',
  color_qr: '#000000',
  color_fondo: '#ffffff',
});

const resolverTextoPreview = (elemento, clasesEjemplo) => {
  if (elemento.fuente_dato === 'texto_fijo') return elemento.texto_fijo || 'Texto';
  if (elemento.fuente_dato === 'cuit_cuil') return formatearCuil(VALORES_EJEMPLO.cuit_cuil) || '';
  // Clases: las del contribuyente de ejemplo (las primeras del nomenclador del municipio).
  if (elemento.fuente_dato === 'clases') return textoClasesSoloCodigos(clasesEjemplo);
  return VALORES_EJEMPLO[elemento.fuente_dato] || '';
};

const alineacionATextAlign = { izquierda: 'left', centro: 'center', derecha: 'right' };

const CREADORES_FORMA = {
  linea: crearElementoLineaDefault,
  rectangulo: crearElementoRectanguloDefault,
  circulo: crearElementoCirculoDefault,
  qr: crearElementoQRDefault,
};

const TITULOS_PROPIEDADES = {
  texto: 'Propiedades del texto',
  imagen: 'Propiedades de la imagen',
  linea: 'Propiedades de la línea',
  rectangulo: 'Propiedades del rectángulo',
  circulo: 'Propiedades del círculo',
  qr: 'Propiedades del código QR',
};

const DisenadorEditor = ({ modo, disenoInicial, tipoImpresionInicial, tipoTarjetaInicial, onGuardado, onCancelar }) => {
  // Clases del contribuyente de ejemplo: salen del nomenclador del municipio (ver utils/clasesLicencia.js).
  const { catalogo } = useClasesMunicipio();
  useTipografiasListas();
  const clasesEjemplo = useMemo(() => clasesDeEjemplo(catalogo), [catalogo]);
  const contribuyenteEjemplo = useMemo(() => ({ ...CONTRIBUYENTE_EJEMPLO, clases: clasesEjemplo }), [clasesEjemplo]);
  const [nombre, setNombre] = useState(disenoInicial?.nombre || '');
  const [colorPrimario, setColorPrimario] = useState(disenoInicial?.color_primario || '#1a9b8e');
  const [colorSecundario, setColorSecundario] = useState(disenoInicial?.color_secundario || '#6b2d7f');
  const [tipoFondo, setTipoFondo] = useState(disenoInicial?.tipo_fondo || 'gradiente');
  const [tipoImpresion, setTipoImpresion] = useState(disenoInicial?.tipo_impresion || tipoImpresionInicial || '');
  const [tipoTarjeta, setTipoTarjeta] = useState(disenoInicial?.tipo_tarjeta || tipoTarjetaInicial || '');
  const [logoUrlActual, setLogoUrlActual] = useState(disenoInicial?.logo_url || null);
  // logoUrlActual nunca es falsy (el backend cae al placeholder genérico de
  // Betech si el municipio no subió ninguno) — esta bandera aparte es la
  // única forma de saber si hay un logo propio de verdad, para decidir si
  // "Logo del municipio" debe abrir el selector de archivos o no.
  const [tieneLogoPropio, setTieneLogoPropio] = useState(!!disenoInicial?.tiene_logo_propio);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [fondoImagenFrenteUrl, setFondoImagenFrenteUrl] = useState(disenoInicial?.fondo_imagen_frente_url || null);
  const [fondoImagenDorsoUrl, setFondoImagenDorsoUrl] = useState(disenoInicial?.fondo_imagen_dorso_url || null);
  const [subiendoFondoFrente, setSubiendoFondoFrente] = useState(false);
  const [subiendoFondoDorso, setSubiendoFondoDorso] = useState(false);
  // Advertencia no bloqueante (ej. imagen de baja resolución) — a
  // diferencia de "error", no impide que la imagen se haya guardado.
  const [advertenciaFondo, setAdvertenciaFondo] = useState('');
  const [modalImpresionAbierto, setModalImpresionAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // 'idle' | 'guardando' | 'guardado' — solo para el botón "Guardar" (no navega).
  const [estadoGuardado, setEstadoGuardado] = useState('idle');
  const [error, setError] = useState('');
  // El aviso de recuperación solo aplica al editar un diseño existente que
  // ya tiene un borrador más nuevo que lo confirmado (nunca al crear uno).
  const [mostrarAvisoBorrador, setMostrarAvisoBorrador] = useState(
    modo === 'editar' && !!disenoInicial?.tiene_borrador_pendiente
  );
  // 'idle' | 'guardando' | 'guardado'
  const [estadoBorrador, setEstadoBorrador] = useState('idle');

  // ----- Estado del canvas de posiciones (solo aplica en modo "editar") -----
  const [cara, setCara] = useState('frente');
  const [elementosFrente, setElementosFrente] = useState(disenoInicial?.layout_frente || []);
  const [elementosDorso, setElementosDorso] = useState(disenoInicial?.layout_dorso || []);
  // Selección: lista de ids (varios con Ctrl/Cmd+clic o Ctrl+A). `seleccionadoId` (más abajo) sigue siendo "EL
  // elemento seleccionado" cuando hay exactamente uno: así el panel de propiedades de un elemento, las manijas de
  // redimensionar, Supr, las flechas y el arrastre quedan igual que siempre. Con 2 o más solo se editan, en
  // bloque, las propiedades de texto (color, tamaño y estilo): no se mueve ni se redimensiona en grupo.
  const [seleccionIds, setSeleccionIds] = useState([]);
  // Texto que se está tipeando en el campo "Tamaño de fuente" de la selección múltiple (se aplica al perder el
  // foco o con Enter; antes si ya es un número válido). `clave` lo ata a la selección en curso.
  const [tamanoMultiBorrador, setTamanoMultiBorrador] = useState({ clave: null, texto: null });
  const [menuImagenAbierto, setMenuImagenAbierto] = useState(false);
  // Candado de proporción del panel "Posición y tamaño": mientras está activo, cambiar el ancho (o el alto) de lo
  // seleccionado cambia también el otro lado. El QR siempre queda cuadrado, con o sin candado.
  const [proporcionBloqueada, setProporcionBloqueada] = useState(false);

  const borradorTimeoutRef = useRef(null);
  const indicadorTimeoutRef = useRef(null);
  const confirmacionTimeoutRef = useRef(null);
  const primerRenderBorradorRef = useRef(true);
  // Tras un guardado real, persistirDiseno() re-setea elementosFrente/
  // elementosDorso con la versión normalizada (nuevas referencias de
  // array). Eso solo, sin este flag, dispararía el efecto de autoguardado
  // de borrador otra vez 2.5s después de guardar de verdad — y como el PUT
  // real ya deja borrador=NULL, ese borrador "fantasma" volvería a marcar
  // el diseño como "con cambios sin guardar" la próxima vez que se abra.
  const saltarProximoBorradorRef = useRef(false);
  const canvasRef = useRef(null);
  const arrastreRef = useRef(null);
  const logoInputRef = useRef(null);
  // El <input type="file"> del logo es uno solo, compartido por los dos
  // disparadores posibles (onElegirLogoMunicipio y el botón "Cambiar logo"
  // del panel de propiedades) — esta ref le dice a alElegirArchivoLogo, una
  // vez que el usuario ya eligió el archivo, si además de subirlo hay que
  // agregar un elemento nuevo al canvas o no. Un ref y no un state porque no
  // necesita disparar ningún render: solo lo lee el propio handler, después.
  const modoSeleccionLogoRef = useRef('agregar');

  useEffect(() => {
    return () => {
      if (borradorTimeoutRef.current) clearTimeout(borradorTimeoutRef.current);
      if (indicadorTimeoutRef.current) clearTimeout(indicadorTimeoutRef.current);
      if (confirmacionTimeoutRef.current) clearTimeout(confirmacionTimeoutRef.current);
    };
  }, []);

  // Autoguardado de borrador: solo al editar un diseño ya existente (en
  // "crear" todavía no hay id para guardarle nada). Cubre nombre/colores/
  // config de impresión Y el layout del canvas — todo vive en la misma
  // pantalla ahora, así que el borrador tiene que reflejarlo todo.
  useEffect(() => {
    if (modo !== 'editar' || mostrarAvisoBorrador) return;

    // No autoguardar apenas se monta con los valores tal cual vinieron.
    if (primerRenderBorradorRef.current) {
      primerRenderBorradorRef.current = false;
      return;
    }

    if (saltarProximoBorradorRef.current) {
      saltarProximoBorradorRef.current = false;
      return;
    }

    if (borradorTimeoutRef.current) clearTimeout(borradorTimeoutRef.current);
    borradorTimeoutRef.current = setTimeout(async () => {
      try {
        setEstadoBorrador('guardando');
        const token = sessionStorage.getItem('token');
        await axios.put(
          `${API_URL}/municipios/disenos/${disenoInicial.id}/borrador`,
          {
            nombre,
            color_primario: colorPrimario,
            color_secundario: colorSecundario,
            tipo_fondo: tipoFondo,
            tipo_impresion: tipoImpresion,
            tipo_tarjeta: tipoTarjeta,
            layout_frente: elementosFrente,
            layout_dorso: elementosDorso,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEstadoBorrador('guardado');
        if (indicadorTimeoutRef.current) clearTimeout(indicadorTimeoutRef.current);
        indicadorTimeoutRef.current = setTimeout(() => setEstadoBorrador('idle'), DURACION_INDICADOR_OK_MS);
      } catch (err) {
        console.error('Error al guardar el borrador:', err);
        setEstadoBorrador('idle');
      }
    }, DEBOUNCE_BORRADOR_MS);

    return () => {
      if (borradorTimeoutRef.current) clearTimeout(borradorTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombre, colorPrimario, colorSecundario, tipoFondo, tipoImpresion, tipoTarjeta, elementosFrente, elementosDorso, modo, mostrarAvisoBorrador]);

  const recuperarBorrador = () => {
    const b = disenoInicial?.borrador || {};
    if (b.nombre !== undefined && b.nombre !== null) setNombre(b.nombre);
    if (b.color_primario) setColorPrimario(b.color_primario);
    if (b.color_secundario) setColorSecundario(b.color_secundario);
    if (b.tipo_fondo) setTipoFondo(b.tipo_fondo);
    if (b.tipo_impresion) setTipoImpresion(b.tipo_impresion);
    if (b.tipo_tarjeta) setTipoTarjeta(b.tipo_tarjeta);
    if (Array.isArray(b.layout_frente)) setElementosFrente(b.layout_frente);
    if (Array.isArray(b.layout_dorso)) setElementosDorso(b.layout_dorso);
    setMostrarAvisoBorrador(false);
  };

  const descartarBorrador = () => {
    setMostrarAvisoBorrador(false);
  };

  const alElegirConfigImpresion = (tipoImpresionElegido, tipoTarjetaElegida) => {
    setTipoImpresion(tipoImpresionElegido);
    setTipoTarjeta(tipoTarjetaElegida);
    setModalImpresionAbierto(false);
    // Si pasa a "una cara" estando parado en la pestaña Dorso, las pestañas
    // desaparecen pero sin este reset el canvas seguiría mostrando/editando
    // el dorso ahora oculto, sin ninguna forma de notarlo en pantalla.
    if (tipoImpresionElegido !== 'doble_cara') {
      setCara('frente');
    }
  };

  // ----- Crear (sin canvas todavía: no hay id) -----
  const guardarNuevo = async () => {
    if (!nombre.trim()) {
      setError('Ponele un nombre al diseño');
      return;
    }
    try {
      setGuardando(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/municipios/disenos`,
        {
          nombre: nombre.trim(),
          color_primario: colorPrimario,
          color_secundario: colorSecundario,
          tipo_fondo: tipoFondo,
          tipo_impresion: tipoImpresion,
          tipo_tarjeta: tipoTarjeta,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      onGuardado(data.diseno);
    } catch (err) {
      console.error('Error al crear el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al crear el diseño');
    } finally {
      setGuardando(false);
    }
  };

  // ----- Editar: guardado real (nombre + colores + layout, todo junto) -----
  const persistirDiseno = async () => {
    const token = sessionStorage.getItem('token');
    const layoutFrenteNormalizado = normalizarElementos(elementosFrente);
    const layoutDorsoNormalizado = normalizarElementos(elementosDorso);
    const { data } = await axios.put(
      `${API_URL}/municipios/disenos/${disenoInicial.id}`,
      {
        nombre: nombre.trim(),
        color_primario: colorPrimario,
        color_secundario: colorSecundario,
        tipo_fondo: tipoFondo,
        tipo_impresion: tipoImpresion,
        tipo_tarjeta: tipoTarjeta,
        layout_frente: layoutFrenteNormalizado,
        layout_dorso: layoutDorsoNormalizado,
      },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    saltarProximoBorradorRef.current = true;
    setElementosFrente(layoutFrenteNormalizado);
    setElementosDorso(layoutDorsoNormalizado);
    return data;
  };

  // "Guardar": persiste y se queda en la pantalla.
  const guardar = async () => {
    if (!nombre.trim()) {
      setError('Ponele un nombre al diseño');
      return;
    }
    if (borradorTimeoutRef.current) clearTimeout(borradorTimeoutRef.current);
    try {
      setGuardando(true);
      setEstadoGuardado('guardando');
      setError('');
      await persistirDiseno();
      setEstadoGuardado('guardado');
      confirmacionTimeoutRef.current = setTimeout(() => setEstadoGuardado('idle'), DURACION_CONFIRMACION_MS);
    } catch (err) {
      console.error('Error al guardar el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al guardar el diseño');
      setEstadoGuardado('idle');
    } finally {
      setGuardando(false);
    }
  };

  // "✓ Listo, volver a mis diseños": la única acción que efectivamente saca
  // al usuario de la pantalla — guarda antes de salir para no dejar la
  // galería/vista previa mostrando una versión vieja.
  const guardarYSalir = async () => {
    if (!nombre.trim()) {
      setError('Ponele un nombre al diseño');
      return;
    }
    if (borradorTimeoutRef.current) clearTimeout(borradorTimeoutRef.current);
    try {
      setGuardando(true);
      setError('');
      const data = await persistirDiseno();
      onGuardado(data.diseno);
    } catch (err) {
      console.error('Error al guardar el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al guardar el diseño');
      setGuardando(false);
    }
  };

  // ----- Logo del municipio: ahora es un elemento más del canvas, y todos
  // los elementos con fuente_imagen: 'logo_municipio' leen el mismo
  // logoUrlActual — por eso subirLogoArchivo() nunca necesita tocar
  // elementosActivos, alcanza con actualizar ese estado compartido para que
  // se refresquen solos. Lo que sí cambia según de dónde se dispare
  // (modoSeleccionLogoRef, seteado por quien llama a logoInputRef.click())
  // es si ADEMÁS hay que agregar un elemento nuevo al canvas:
  //  - 'agregar' (onElegirLogoMunicipio, cuando el diseño todavía no tiene
  //    ningún logo): sube y coloca el primer elemento.
  //  - 'reemplazar' (botón "Cambiar logo" del panel de propiedades, sobre un
  //    elemento que ya está en el canvas): solo reemplaza el archivo — el
  //    elemento ya existente se actualiza solo al re-renderizar. -----
  const subirLogoArchivo = async (file) => {
    try {
      setSubiendoLogo(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await axios.put(
        `${API_URL}/municipios/disenos/${disenoInicial.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setLogoUrlActual(data.diseno.logo_url);
      setTieneLogoPropio(true);
      if (modoSeleccionLogoRef.current === 'agregar') {
        agregarImagen('logo_municipio');
      }
    } catch (err) {
      console.error('Error al subir el logo:', err);
      setError(err.response?.data?.mensaje || 'Error al subir el logo');
    } finally {
      setSubiendoLogo(false);
    }
  };

  const alElegirArchivoLogo = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    subirLogoArchivo(file);
  };

  const onElegirLogoMunicipio = () => {
    setMenuImagenAbierto(false);
    if (tieneLogoPropio) {
      agregarImagen('logo_municipio');
    } else {
      modoSeleccionLogoRef.current = 'agregar';
      logoInputRef.current?.click();
    }
  };

  // Botón "Cambiar logo" del panel de propiedades (visible con un elemento
  // logo_municipio ya seleccionado) — a diferencia de onElegirLogoMunicipio,
  // dispara el picker de archivo SIEMPRE, incluso si ya hay un logo subido:
  // es la única vía para reemplazarlo una vez que existe uno.
  const onCambiarLogo = () => {
    modoSeleccionLogoRef.current = 'reemplazar';
    logoInputRef.current?.click();
  };

  // ----- Imagen de fondo por cara (tarjeta blanca + fondo personalizado, o
  // tarjeta preimpresa — ver la nota de "regla de negocio crítica" sobre
  // preimpresas en el backend, resolverDisenoIndividual()). Mismo endpoint
  // valida proporción (obligatorio, nunca estira) y resolución (advertencia
  // no bloqueante) antes de guardar. -----
  const subirFondo = async (caraImagen, file) => {
    const setSubiendo = caraImagen === 'frente' ? setSubiendoFondoFrente : setSubiendoFondoDorso;
    try {
      setSubiendo(true);
      setError('');
      setAdvertenciaFondo('');
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append(`fondo_imagen_${caraImagen}`, file);
      const { data } = await axios.put(
        `${API_URL}/municipios/disenos/${disenoInicial.id}/fondo`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (caraImagen === 'frente') setFondoImagenFrenteUrl(data.diseno.fondo_imagen_frente_url);
      else setFondoImagenDorsoUrl(data.diseno.fondo_imagen_dorso_url);
      const advertencia = data.advertencias?.[caraImagen];
      if (advertencia) setAdvertenciaFondo(advertencia);
    } catch (err) {
      console.error('Error al subir la imagen de fondo:', err);
      setError(err.response?.data?.mensaje || 'Error al subir la imagen de fondo');
    } finally {
      setSubiendo(false);
    }
  };

  const alElegirArchivoFondo = (caraImagen) => (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    subirFondo(caraImagen, file);
  };

  // ----- Canvas: drag/resize/agregar/propiedades (igual que sub-fase 1+2) -----
  const esDobleCara = tipoImpresion === 'doble_cara';
  const elementosActivos = cara === 'frente' ? elementosFrente : elementosDorso;
  const setElementosActivos = cara === 'frente' ? setElementosFrente : setElementosDorso;
  // Ids que de verdad existen en la cara activa (un id viejo —de la otra cara, de un elemento borrado o de un
  // borrador recuperado— se ignora solo).
  const idsSeleccionados = seleccionIds.filter((id) => elementosActivos.some((el) => el.id === id));
  const seleccionMultiple = idsSeleccionados.length > 1;
  const seleccionadoId = idsSeleccionados.length === 1 ? idsSeleccionados[0] : null;
  const setSeleccionadoId = (id) => setSeleccionIds(id ? [id] : []);
  const elementoSeleccionado = elementosActivos.find((el) => el.id === seleccionadoId) || null;
  const elementosSeleccionados = elementosActivos.filter((el) => idsSeleccionados.includes(el.id));
  const textosSeleccionados = elementosSeleccionados.filter((el) => el.tipo === 'texto');
  const dimTarjeta = dimensionesTarjetaMm(disenoInicial);

  // Panel "Posición y tamaño": aplica UNA medida (en mm, ya validada como número) a todo lo seleccionado, siempre dentro
  // de la tarjeta (ver aplicarMedida en utils/posicionTamano.js). Un único cambio de estado, así que entra de una vez en
  // el autoguardado del borrador. Devuelve el aviso a mostrar si algún valor se tuvo que acotar (o null).
  const aplicarMedidaASeleccion = (campo, valorMm) => {
    const r = aplicarMedidaALista(elementosActivos, idsSeleccionados, campo, valorMm, { dim: dimTarjeta, mantenerProporcion: proporcionBloqueada });
    setElementosActivos(r.elementos);
    return r.mensaje;
  };

  // Botón "Foto 3:4": deja la caja de cada foto del contribuyente seleccionada en proporción 3:4 manteniendo el ancho.
  const aplicarFoto34ASeleccion = () => {
    const r = aplicarFoto34ALista(elementosActivos, idsSeleccionados, dimTarjeta);
    setElementosActivos(r.elementos);
    return r.mensaje;
  };

  // Campo "clases" en formato código + descripción: el texto ya achicado/cortado, calculado con la misma
  // función que usan la vista previa y la impresión (utils/formatoClases.js). null para cualquier otro
  // elemento (o para el formato "solo códigos", que no cambia).
  const ajusteClasesDe = (el) => (
    el.tipo === 'texto' && el.fuente_dato === 'clases' && formatoDeElemento(el) === FORMATO_CODIGO_DESCRIPCION
      ? resolverClasesCodigoDescripcion({ clases: clasesEjemplo, catalogo, elemento: el })
      : null
  );

  const cambiarCara = (nuevaCara) => {
    setCara(nuevaCara);
    setSeleccionadoId(null);
    setMenuImagenAbierto(false);
  };

  // Ctrl/Cmd+clic sobre un elemento: lo suma o lo saca de la selección (sin empezar ningún arrastre).
  const alternarSeleccion = (e, elemento) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuImagenAbierto(false);
    setSeleccionIds((prev) => {
      const vigentes = prev.filter((id) => elementosActivos.some((el) => el.id === id));
      return vigentes.includes(elemento.id)
        ? vigentes.filter((id) => id !== elemento.id)
        : [...vigentes, elemento.id];
    });
  };

  // Cambio en bloque: un solo setState sobre la cara activa, solo para los TEXTOS de la selección (los demás
  // elementos —foto, imagen, formas, QR— se ignoran sin error). Al ser un único cambio de estado, entra de una
  // vez en el autoguardado del borrador igual que cualquier otro cambio del lienzo.
  const actualizarCampoTextosSeleccionados = (campo, valor) => {
    const ids = new Set(textosSeleccionados.map((el) => el.id));
    setElementosActivos((prev) => prev.map((el) => (
      el.tipo === 'texto' && ids.has(el.id) ? { ...el, [campo]: valor } : el
    )));
  };

  // Valor común de una propiedad entre los textos seleccionados, o { mixto: true } si no coinciden.
  const comunDeTextos = (leer) => {
    if (textosSeleccionados.length === 0) return { mixto: false, valor: undefined };
    const valores = textosSeleccionados.map(leer);
    return { mixto: valores.some((v) => v !== valores[0]), valor: valores[0] };
  };
  const colorMulti = comunDeTextos((el) => String(el.color || '').toLowerCase());
  const tamanoMulti = comunDeTextos((el) => Number(el.tamano_fuente) || 12);
  const negritaMulti = comunDeTextos((el) => !!el.negrita);
  const cursivaMulti = comunDeTextos((el) => !!el.cursiva);
  const claveSeleccion = idsSeleccionados.join('|');
  const textoTamanoMulti = tamanoMultiBorrador.clave === claveSeleccion ? tamanoMultiBorrador.texto : null;
  // Mixto -> campo vacío (placeholder "Mixto"), igual que en los otros valores.
  const valorTamanoMulti = textoTamanoMulti !== null
    ? textoTamanoMulti
    : (tamanoMulti.mixto || tamanoMulti.valor === undefined ? '' : String(tamanoMulti.valor));

  const alCambiarTamanoMulti = (e) => {
    const texto = e.target.value;
    setTamanoMultiBorrador({ clave: claveSeleccion, texto });
    // Si ya es un entero válido se aplica en el momento (también con las flechitas del campo); si no
    // (por ejemplo "1" camino a "12"), se espera al blur/Enter, donde se acota a 6–72.
    if (/^\d{1,2}$/.test(texto) && Number(texto) >= 6 && Number(texto) <= 72) {
      actualizarCampoTextosSeleccionados('tamano_fuente', Number(texto));
    }
  };

  const confirmarTamanoMulti = (e) => {
    const texto = e.target.value;
    // Vacío y mixto: no se elige ningún valor, queda "Mixto".
    if (texto !== '') actualizarCampoTextosSeleccionados('tamano_fuente', clamp(Number(texto) || 12, 6, 72));
    setTamanoMultiBorrador({ clave: null, texto: null });
  };

  // Negrita/cursiva con tres estados: todos activos, ninguno, o mixto. Un clic en "mixto" los activa a todos.
  const alternarEstiloMulti = (campo, estado) => {
    actualizarCampoTextosSeleccionados(campo, estado.mixto ? true : !estado.valor);
  };

  const agregarTexto = () => {
    const nuevo = crearElementoTextoDefault();
    setElementosActivos((prev) => [...prev, nuevo]);
    setSeleccionadoId(nuevo.id);
  };

  const agregarImagen = (fuenteImagen) => {
    const nuevo = crearElementoImagenDefault(fuenteImagen);
    setElementosActivos((prev) => [...prev, nuevo]);
    setSeleccionadoId(nuevo.id);
    setMenuImagenAbierto(false);
  };

  const agregarForma = (tipoForma) => {
    const nuevo = CREADORES_FORMA[tipoForma]();
    setElementosActivos((prev) => [...prev, nuevo]);
    setSeleccionadoId(nuevo.id);
  };

  // Se recrea en cada render a propósito (no useCallback): el useEffect del
  // atajo de teclado, más abajo, la lista como dependencia justamente para
  // re-registrar el listener con la versión fresca — mismo criterio que el
  // useEffect del autoguardado.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eliminarSeleccionado = () => {
    setElementosActivos((prev) => prev.filter((el) => el.id !== seleccionadoId));
    setSeleccionadoId(null);
  };

  // Mismo criterio que eliminarSeleccionado (se recrea cada render, va en las
  // dependencias del useEffect del atajo). A diferencia del arrastre con
  // mouse — que parte de un snapshot (elInicial) + delta acumulado —, acá
  // cada pulsación es un paso incremental sobre el valor ACTUAL del elemento.
  // Mismo helper clamp y mismos límites que el modo 'mover' del arrastre:
  // x en [0, 100 - width], y en [0, 100 - height] (el elemento nunca se sale
  // del lienzo; contra el borde, la flecha simplemente no mueve más).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const moverSeleccionadoConFlecha = (dx, dy) => {
    setElementosActivos((prev) => prev.map((el) => (
      el.id === seleccionadoId
        ? {
            ...el,
            x: clamp(el.x + dx, 0, 100 - el.width),
            y: clamp(el.y + dy, 0, 100 - el.height),
          }
        : el
    )));
  };

  // Atajo de teclado sobre el elemento seleccionado del canvas:
  //   - Supr / Backspace → borra (camino adicional al botón "Eliminar
  //     elemento", llama a la MISMA función, no duplica lógica).
  //   - Flechas → mueve PASO_MM mm (PASO_MM_SHIFT con Shift), reusando la
  //     misma forma de actualizar el estado que el arrastre con mouse.
  //
  // Un solo listener de keydown para todo. El listener se re-registra en
  // cada render (las funciones y flags de la dependencia son valores nuevos
  // por render) — mismo criterio que el useEffect del autoguardado más
  // abajo: dependencias, no refs. add/remove de un solo listener por render
  // es barato y deja el código lineal.
  //
  // Solo en modo "editar": en "crear" no hay canvas ni elementos.
  useEffect(() => {
    if (modo !== 'editar') return;

    const alApretarTecla = (e) => {
      const esBorrar = e.key === 'Delete' || e.key === 'Backspace';
      const esFlecha =
        e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      // Ctrl+A (Cmd+A en Mac): selecciona todos los elementos de la cara que se está editando.
      const esSeleccionarTodo = (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'a' || e.key === 'A');
      // Escape: limpia la selección.
      const esEscape = e.key === 'Escape';
      if (!esBorrar && !esFlecha && !esSeleccionarTodo && !esEscape) return;

      // 1. Foco en un campo editable → la tecla es del campo, no del canvas:
      //    borrar texto en un input, mover el cursor con las flechas dentro
      //    de un <input>, cambiar de opción con las flechas en un <select>.
      const activo = document.activeElement;
      const tag = activo?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || activo?.isContentEditable) {
        return;
      }

      // 2. Hay un overlay abierto que se lleva la atención del usuario:
      //    - modalImpresionAbierto  → ConfiguracionImpresionModal
      //    - mostrarAvisoBorrador    → AvisoBorradorModal
      //    - menuImagenAbierto       → dropdown "Agregar imagen" (no es un
      //      modal, pero mismo espíritu: UI transitoria abierta encima del
      //      canvas, no queremos tocar algo "de fondo").
      if (modalImpresionAbierto || mostrarAvisoBorrador || menuImagenAbierto) {
        return;
      }

      if (esSeleccionarTodo) {
        e.preventDefault();
        setSeleccionIds(elementosActivos.map((el) => el.id));
        return;
      }
      if (esEscape) {
        if (idsSeleccionados.length > 0) {
          e.preventDefault();
          setSeleccionIds([]);
        }
        return;
      }

      // 3. No hay UN elemento seleccionado para borrar/mover (con varios seleccionados, Supr y las flechas
      //    no hacen nada: en esta versión no se borra ni se mueve en grupo).
      if (!seleccionadoId) return;

      if (esBorrar) {
        // Backspace, sin foco en un campo, puede disparar "volver atrás" del
        // navegador en algún caso raro — lo cortamos antes de borrar.
        e.preventDefault();
        eliminarSeleccionado();
        return;
      }

      // Flechas: mover el elemento. preventDefault evita el scroll de la página.
      e.preventDefault();
      const factor = e.shiftKey ? PASO_MM_SHIFT : PASO_MM;
      const pasoX = (factor / ANCHO_TARJETA_MM) * 100;
      const pasoY = (factor / ALTO_TARJETA_MM) * 100;
      if (e.key === 'ArrowUp') moverSeleccionadoConFlecha(0, -pasoY);
      else if (e.key === 'ArrowDown') moverSeleccionadoConFlecha(0, pasoY);
      else if (e.key === 'ArrowLeft') moverSeleccionadoConFlecha(-pasoX, 0);
      else if (e.key === 'ArrowRight') moverSeleccionadoConFlecha(pasoX, 0);
    };

    window.addEventListener('keydown', alApretarTecla);
    return () => window.removeEventListener('keydown', alApretarTecla);
  }, [modo, seleccionadoId, idsSeleccionados, elementosActivos, modalImpresionAbierto, mostrarAvisoBorrador, menuImagenAbierto, eliminarSeleccionado, moverSeleccionadoConFlecha]);

  const actualizarCampoSeleccionado = (campo, valor) => {
    setElementosActivos((prev) => prev.map((el) => (el.id === seleccionadoId ? { ...el, [campo]: valor } : el)));
  };

  // El input de tamaño de fuente/rotación deja escribir libre (incluso
  // vacío) sin forzar un valor por default en cada tecla — eso era lo que
  // rompía tipear "12" y terminaba en "612": ni bien el campo quedaba vacío
  // por un instante, el fallback lo pisaba con 6 y la tecla siguiente se
  // sumaba a ese 6, no reemplazaba lo que se estaba por borrar. Se valida/
  // clampea recién al perder foco (o Enter, que dispara blur).
  const confirmarTamanoFuente = (e) => {
    const valor = clamp(Number(e.target.value) || 12, 6, 72);
    actualizarCampoSeleccionado('tamano_fuente', valor);
  };

  const confirmarRotacion = (e) => {
    const valor = clamp(Number(e.target.value) || 0, -180, 180);
    actualizarCampoSeleccionado('rotacion', valor);
  };

  const confirmarGrosor = (e) => {
    const valor = clamp(Number(e.target.value) || 2, 1, 20);
    actualizarCampoSeleccionado('grosor', valor);
  };

  const confirmarGrosorBorde = (e) => {
    const valor = clamp(Number(e.target.value) || 2, 1, 20);
    actualizarCampoSeleccionado('grosor_borde', valor);
  };

  const confirmarRadioEsquina = (e) => {
    const valor = clamp(Number(e.target.value) || 0, 0, 100);
    actualizarCampoSeleccionado('radio_esquina', valor);
  };

  const alSoltarMouse = () => {
    arrastreRef.current = null;
    document.removeEventListener('mousemove', alMoverMouse);
    document.removeEventListener('mouseup', alSoltarMouse);
  };

  const alMoverMouse = (e) => {
    const a = arrastreRef.current;
    if (!a) return;

    const deltaXpct = ((e.clientX - a.startClientX) / a.canvasWidth) * 100;
    const deltaYpct = ((e.clientY - a.startClientY) / a.canvasHeight) * 100;

    a.setElementos((prev) => prev.map((el) => {
      if (el.id !== a.id) return el;
      const base = a.elInicial;

      if (a.modo === 'mover') {
        return {
          ...el,
          x: clamp(base.x + deltaXpct, 0, 100 - base.width),
          y: clamp(base.y + deltaYpct, 0, 100 - base.height),
        };
      }

      // QR: nunca se estira a rectángulo (un QR deformado no escanea). El
      // ancho manda (según hacia dónde se arrastra horizontalmente) y el
      // alto se deriva con RATIO_TARJETA para que se vea cuadrado en
      // pantalla — acordate que width%/height% son porcentajes de dos ejes
      // distintos (ancho y alto del lienzo), no la misma unidad, así que
      // width===height NO es "cuadrado" visualmente acá.
      if (el.tipo === 'qr') {
        const dirE = a.esquina.includes('e');
        const dirN = a.esquina.includes('n');
        const deltaAncho = dirE ? deltaXpct : -deltaXpct;
        const maxAnchoHorizontal = dirE ? (100 - base.x) : (base.x + base.width);
        const maxAltoVertical = dirN ? (base.y + base.height) : (100 - base.y);
        const maxAnchoPorAlto = maxAltoVertical / RATIO_TARJETA;
        const anchoMaximoPermitido = Math.min(maxAnchoHorizontal, maxAnchoPorAlto);
        const nuevoAncho = clamp(base.width + deltaAncho, MIN_SIZE_PCT, Math.max(anchoMaximoPermitido, MIN_SIZE_PCT));
        const nuevoAlto = nuevoAncho * RATIO_TARJETA;
        return {
          ...el,
          width: nuevoAncho,
          height: nuevoAlto,
          x: dirE ? base.x : base.x + base.width - nuevoAncho,
          y: dirN ? base.y + base.height - nuevoAlto : base.y,
        };
      }

      let { x, y, width, height } = base;
      // Mínimo del arrastre: MIN_SIZE_PCT, pero nunca más que el tamaño que ya tiene (un elemento que se fijó a
      // mano más chico —una línea fina de 1,5 mm, por ejemplo— no pega un salto al tomarlo de una manija).
      const minAncho = Math.min(MIN_SIZE_PCT, base.width);
      const minAlto = Math.min(MIN_SIZE_PCT, base.height);
      if (a.esquina.includes('e')) width = clamp(base.width + deltaXpct, minAncho, 100 - base.x);
      if (a.esquina.includes('s')) height = clamp(base.height + deltaYpct, minAlto, 100 - base.y);
      if (a.esquina.includes('w')) {
        const nuevoAncho = clamp(base.width - deltaXpct, minAncho, base.x + base.width);
        x = base.x + base.width - nuevoAncho;
        width = nuevoAncho;
      }
      if (a.esquina.includes('n')) {
        const nuevoAlto = clamp(base.height - deltaYpct, minAlto, base.y + base.height);
        y = base.y + base.height - nuevoAlto;
        height = nuevoAlto;
      }
      return { ...el, x, y, width, height };
    }));
  };

  const iniciarArrastre = (e, elemento, modoArrastre, esquina) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();

    arrastreRef.current = {
      id: elemento.id,
      modo: modoArrastre,
      esquina,
      startClientX: e.clientX,
      startClientY: e.clientY,
      canvasWidth: rect.width,
      canvasHeight: rect.height,
      elInicial: { ...elemento },
      setElementos: setElementosActivos,
    };

    setSeleccionadoId(elemento.id);
    document.addEventListener('mousemove', alMoverMouse);
    document.addEventListener('mouseup', alSoltarMouse);
  };

  // Red de seguridad: si se guarda sin que el campo de tamaño de fuente o
  // rotación haya perdido el foco todavía, puede seguir siendo el string
  // crudo que se estaba tipeando. Nunca debe llegar así al backend.
  const normalizarElementos = (elementos) => elementos.map((el) => ({
    ...el,
    ...(el.tipo === 'texto' ? { tamano_fuente: clamp(Number(el.tamano_fuente) || 12, 6, 72) } : {}),
    ...(el.tipo === 'linea' ? { grosor: clamp(Number(el.grosor) || 2, 1, 20) } : {}),
    ...((el.tipo === 'rectangulo' || el.tipo === 'circulo') && el.color_borde
      ? { grosor_borde: clamp(Number(el.grosor_borde) || 2, 1, 20) }
      : {}),
    ...(el.tipo === 'rectangulo' ? { radio_esquina: clamp(Number(el.radio_esquina) || 0, 0, 100) } : {}),
    // Red de seguridad extra para el QR: fuerza el cuadrado visual pase lo
    // que pase durante el drag, para que nunca se guarde deformado.
    ...(el.tipo === 'qr' ? { height: el.width * RATIO_TARJETA } : {}),
    rotacion: clamp(Number(el.rotacion) || 0, -180, 180),
  }));

  const fondoLienzo = tipoFondo === 'solido'
    ? colorPrimario
    : `linear-gradient(135deg, ${colorPrimario}, ${colorSecundario})`;

  // Tarjeta preimpresa: la imagen de fondo se muestra siempre (es la ayuda
  // visual para ver cómo va a quedar la tarjeta real), independiente del
  // "Tipo de fondo" elegido. Tarjeta blanca: solo si se eligió
  // explícitamente "Imagen personalizada" como tipo de fondo.
  const usaFondoImagen = tipoTarjeta === 'preimpresa' || tipoFondo === 'imagen_personalizada';
  const fondoImagenActual = cara === 'frente' ? fondoImagenFrenteUrl : fondoImagenDorsoUrl;
  const estiloFondoLienzo = usaFondoImagen
    ? (fondoImagenActual
        ? { backgroundImage: `url(${fondoImagenActual})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: '#e4e7eb' })
    : { background: fondoLienzo };

  // ===================== MODO CREAR: formulario simple =====================
  if (modo === 'crear') {
    const disenoPreview = {
      color_primario: colorPrimario,
      color_secundario: colorSecundario,
      tipo_fondo: tipoFondo,
      logo_url: '/logo-betech.svg',
      layout_frente: null,
      layout_dorso: null,
    };

    return (
      <section className="disenador-section">
        <div className="disenador-grid">
          <div className="disenador-form">
            <div className="disenador-form-header">
              <div className="disenador-form-header-titulo">
                <button type="button" className="btn-volver" onClick={onCancelar} disabled={guardando}>
                  <ArrowLeft size={16} /> Volver
                </button>
                <h2>Nuevo diseño</h2>
              </div>
              <button type="button" className="btn-config-impresion" onClick={() => setModalImpresionAbierto(true)}>
                <IconConfiguracion {...ICONO_BOTON} /> Cambiar configuración de impresión
              </button>
            </div>
            <p className="form-hint">
              Elegí nombre y colores para empezar. El logo y la posición de los datos se arman en el siguiente paso, una vez creado el diseño.
            </p>

            {error && (
              <div className="alert alert-error">
                <IconAdvertencia size={18} />
                <p>{error}</p>
              </div>
            )}

            <div className="form-field">
              <label>Nombre del diseño</label>
              <input
                type="text"
                className="padron-input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Diseño oficial 2026"
              />
            </div>

            <div className="form-field">
              <label>Color primario</label>
              <SelectorColor
                valor={colorPrimario}
                onCambiar={(hex) => setColorPrimario(hex)}
                etiqueta="Color primario"
              />
            </div>

            <div className="form-field">
              <label>Color secundario</label>
              <SelectorColor
                valor={colorSecundario}
                onCambiar={(hex) => setColorSecundario(hex)}
                etiqueta="Color secundario"
              />
            </div>

            {tipoTarjeta !== 'preimpresa' && (
              <div className="form-field">
                <label>Tipo de fondo</label>
                <select className="padron-input" value={tipoFondo} onChange={(e) => setTipoFondo(e.target.value)}>
                  <option value="gradiente">Gradiente (primario → secundario)</option>
                  <option value="solido">Sólido (solo primario)</option>
                </select>
              </div>
            )}

            <div className="disenador-form-botones">
              <button type="button" className="btn-cancelar" onClick={onCancelar} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="btn-guardar disenador-btn-guardar" onClick={guardarNuevo} disabled={guardando}>
                {guardando ? 'Creando...' : 'Crear diseño'}
              </button>
            </div>

            {tipoTarjeta === 'preimpresa' && (
              <p className="form-hint disenador-nota-preimpresa">
                Estos colores no se imprimen en la tarjeta preimpresa — solo se usan para la vista previa en pantalla.
              </p>
            )}
          </div>

          <div className="disenador-preview">
            <h2>Vista previa</h2>
            {tipoImpresion === 'doble_cara' ? (
              <div className="disenador-preview-doble">
                <div className="disenador-preview-cara">
                  <span className="disenador-preview-cara-label">Frente</span>
                  <LicenseCard contribuyente={contribuyenteEjemplo} diseno={disenoPreview} cara="frente" />
                </div>
                <div className="disenador-preview-cara">
                  <span className="disenador-preview-cara-label">Dorso</span>
                  <LicenseCard contribuyente={contribuyenteEjemplo} diseno={disenoPreview} cara="dorso" />
                </div>
              </div>
            ) : (
              <LicenseCard contribuyente={contribuyenteEjemplo} diseno={disenoPreview} />
            )}
          </div>
        </div>

        {modalImpresionAbierto && (
          <ConfiguracionImpresionModal
            valorInicial={{ tipo_impresion: tipoImpresion, tipo_tarjeta: tipoTarjeta }}
            obligatorio={false}
            onClose={() => setModalImpresionAbierto(false)}
            onSeleccionar={alElegirConfigImpresion}
          />
        )}
      </section>
    );
  }

  // ================= MODO EDITAR: pantalla única (form + canvas) =================
  return (
    <section className="disenador-section canvas-section">
      <div className="canvas-header">
        <div className="disenador-form-header-titulo">
          <h2>Editar diseño — {nombre || 'Sin nombre'}</h2>
        </div>
        <div className="disenador-editor-acciones-header">
          <button type="button" className="btn-config-impresion" onClick={() => setModalImpresionAbierto(true)}>
            <IconConfiguracion {...ICONO_BOTON} /> Cambiar configuración de impresión
          </button>
          {estadoBorrador !== 'idle' && (
            <span className="disenador-borrador-indicador">
              {estadoBorrador === 'guardando' ? (
                <><span className="btn-spinner"></span> Guardando borrador...</>
              ) : (
                <><IconExito size={14} /> Borrador guardado</>
              )}
            </span>
          )}
          <button type="button" className="btn-guardar" onClick={guardar} disabled={guardando}>
            {estadoGuardado === 'guardando' && (<><span className="btn-spinner"></span> Guardando...</>)}
            {estadoGuardado === 'guardado' && (<><IconExito size={15} /> Guardado</>)}
            {estadoGuardado === 'idle' && 'Guardar'}
          </button>
          <button type="button" className="btn-listo" onClick={guardarYSalir} disabled={guardando}>
            <IconExito size={14} /> Listo, volver a mis diseños
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          <p>{error}</p>
        </div>
      )}

      <div className="disenador-editor-campos">
        <div className="form-field">
          <label>Nombre del diseño</label>
          <input
            type="text"
            className="padron-input"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Diseño oficial 2026"
          />
        </div>

        <div className="form-field">
          <label>Color primario</label>
          <SelectorColor
            valor={colorPrimario}
            onCambiar={(hex) => setColorPrimario(hex)}
            etiqueta="Color primario"
          />
        </div>

        <div className="form-field">
          <label>Color secundario</label>
          <SelectorColor
            valor={colorSecundario}
            onCambiar={(hex) => setColorSecundario(hex)}
            etiqueta="Color secundario"
          />
        </div>

        {tipoTarjeta !== 'preimpresa' && (
          <div className="form-field">
            <label>Tipo de fondo</label>
            <select className="padron-input" value={tipoFondo} onChange={(e) => setTipoFondo(e.target.value)}>
              <option value="gradiente">Gradiente (primario → secundario)</option>
              <option value="solido">Sólido (solo primario)</option>
              <option value="imagen_personalizada">Imagen personalizada</option>
            </select>
          </div>
        )}
      </div>

      {tipoTarjeta === 'preimpresa' && (
        <p className="form-hint disenador-nota-preimpresa">
          Estos colores no se imprimen en la tarjeta preimpresa — solo se usan para la vista previa en pantalla.
        </p>
      )}

      {(tipoTarjeta === 'preimpresa' || tipoFondo === 'imagen_personalizada') && (
        <div className="disenador-fondo-imagen-section">
          <p className="form-hint disenador-nota-preimpresa">
            {tipoTarjeta === 'preimpresa'
              ? 'Esta imagen es solo para previsualizar en pantalla (canvas y Vista Previa) — la tarjeta preimpresa ya viene con este diseño de fábrica, así que nunca se envía a imprimir.'
              : 'Esta imagen se usa como fondo real: en el canvas, en la Vista Previa y en la impresión.'}
          </p>

          {advertenciaFondo && (
            <div className="alert alert-warning">
              <IconAdvertencia size={18} />
              <p>{advertenciaFondo}</p>
            </div>
          )}

          <div className="disenador-fondo-imagen-grid">
            <div className="form-field">
              <label>Imagen de fondo — Frente</label>
              {fondoImagenFrenteUrl && (
                <img src={fondoImagenFrenteUrl} alt="" className="disenador-fondo-imagen-preview" />
              )}
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="padron-input"
                onChange={alElegirArchivoFondo('frente')}
                disabled={subiendoFondoFrente}
              />
              {subiendoFondoFrente && (
                <span className="form-hint"><span className="btn-spinner"></span> Subiendo...</span>
              )}
            </div>

            {esDobleCara && (
              <div className="form-field">
                <label>Imagen de fondo — Dorso</label>
                {fondoImagenDorsoUrl && (
                  <img src={fondoImagenDorsoUrl} alt="" className="disenador-fondo-imagen-preview" />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="padron-input"
                  onChange={alElegirArchivoFondo('dorso')}
                  disabled={subiendoFondoDorso}
                />
                {subiendoFondoDorso && (
                  <span className="form-hint"><span className="btn-spinner"></span> Subiendo...</span>
                )}
              </div>
            )}
          </div>

          <p className="form-hint">
            PNG o JPG, hasta 8MB. Recomendado ~1013×638px (300 DPI), misma proporción que la tarjeta (≈1.59:1) — si no coincide, se rechaza en vez de estirarla.
          </p>
        </div>
      )}

      {esDobleCara && (
        <div className="canvas-tabs">
          <button type="button" className={cara === 'frente' ? 'activo' : ''} onClick={() => cambiarCara('frente')}>
            Frente
          </button>
          <button type="button" className={cara === 'dorso' ? 'activo' : ''} onClick={() => cambiarCara('dorso')}>
            Dorso
          </button>
        </div>
      )}

      <div className="canvas-grid">
        <div className="canvas-lienzo-wrapper">
          <div
            className="canvas-lienzo"
            style={estiloFondoLienzo}
            ref={canvasRef}
            // Clic en el fondo vacío limpia la selección (con Ctrl/Cmd apretado no: un Ctrl+clic que erra el
            // elemento no tiene que borrar la selección armada).
            onMouseDown={(e) => { if (!(e.ctrlKey || e.metaKey)) setSeleccionadoId(null); setMenuImagenAbierto(false); }}
          >
            {elementosActivos.map((el) => (
              <div
                key={el.id}
                className={`canvas-elemento ${el.tipo === 'texto' ? 'canvas-elemento--texto' : ''} ${ajusteClasesDe(el)?.cortado ? 'canvas-elemento--clases-cortado' : ''} ${el.tipo === 'imagen' ? 'canvas-elemento-imagen-box' : ''} ${(el.tipo === 'rectangulo' || el.tipo === 'circulo' || el.tipo === 'qr') ? 'canvas-elemento-forma-box' : ''} ${idsSeleccionados.includes(el.id) ? 'seleccionado' : ''} ${seleccionMultiple && idsSeleccionados.includes(el.id) ? 'seleccionado-multi' : ''}`}
                style={{
                  left: `${el.x}%`,
                  top: `${el.y}%`,
                  width: `${el.width}%`,
                  height: `${el.height}%`,
                  transform: `rotate(${el.rotacion || 0}deg)`,
                  ...(el.tipo === 'texto' ? {
                    // px calibrado contra un lienzo de 700px, expresado en cqw
                    // para que escale con el ancho real del lienzo — misma
                    // fórmula que LicenseCard (CANVAS_ANCHO_DISENO_PX). Sin
                    // esto, en una ventana intermedia el lienzo mide < 700px
                    // pero la fuente seguía fija en px y desbordaba la caja.
                    fontSize: pxACqw(el.tamano_fuente),
                    textAlign: alineacionATextAlign[el.alineacion] || 'left',
                    color: el.color,
                  } : {}),
                }}
                onMouseDown={(e) => ((e.ctrlKey || e.metaKey) ? alternarSeleccion(e, el) : iniciarArrastre(e, el, 'mover'))}
                // En Mac, Ctrl+clic abre el menú contextual: acá se usa para seleccionar, así que se evita.
                onContextMenu={(e) => { if (e.ctrlKey) e.preventDefault(); }}
              >
                <div className="canvas-elemento-contenido">
                {el.tipo === 'imagen' ? (
                  <div className="canvas-elemento-imagen">
                    {el.fuente_imagen === 'logo_municipio' ? (
                      logoUrlActual ? (
                        <img src={logoUrlActual} alt="" className="canvas-elemento-imagen-img" />
                      ) : (
                        <span className="canvas-elemento-imagen-placeholder"><IconMunicipio size="1em" /><br />Logo</span>
                      )
                    ) : (
                      <span className="canvas-elemento-imagen-placeholder"><IconPersona size="1em" /><br />Foto</span>
                    )}
                  </div>
                ) : el.tipo === 'linea' ? (
                  <div
                    className="canvas-elemento-linea"
                    style={{ height: pxACqw(el.grosor), background: el.color }}
                  />
                ) : el.tipo === 'rectangulo' ? (
                  <div
                    className="canvas-elemento-forma"
                    style={{
                      background: el.color_relleno || 'transparent',
                      border: el.color_borde ? `${pxACqw(el.grosor_borde)} solid ${el.color_borde}` : 'none',
                      borderRadius: pxACqw(el.radio_esquina || 0),
                    }}
                  />
                ) : el.tipo === 'circulo' ? (
                  <div
                    className="canvas-elemento-forma"
                    style={{
                      background: el.color_relleno || 'transparent',
                      border: el.color_borde ? `${pxACqw(el.grosor_borde)} solid ${el.color_borde}` : 'none',
                      borderRadius: '50%',
                    }}
                  />
                ) : el.tipo === 'qr' ? (
                  <div className="canvas-elemento-qr">
                    <QRCodeSVG
                      value={resolverContenidoQR(el, contribuyenteEjemplo) || ' '}
                      size={256}
                      fgColor={el.color_qr}
                      bgColor={el.color_fondo}
                      // marginSize=2: zona de silencio alrededor del QR
                      // (módulos en blanco) — sin esto, un QR pegado a otros
                      // elementos del diseño puede fallar al escanear.
                      marginSize={2}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                ) : (
                  <span
                    className={`canvas-elemento-texto ${ajusteClasesDe(el) ? 'canvas-elemento-texto--clases' : ''}`}
                    style={{
                      ...(ajusteClasesDe(el) ? { fontSize: pxDisenoACqw(ajusteClasesDe(el).fontPx) } : {}),
                      // El fontWeight/fontStyle va en el span (no en el div
                      // padre) porque .canvas-elemento-texto tiene su propia
                      // regla font-weight:600 en disenador.css — esa regla
                      // explícita sobre el propio span gana siempre a lo
                      // heredado del padre, así que ponerlo inline solo en el
                      // div nunca se veía reflejado acá.
                      // 800 a propósito, no 700: Inter solo tenía 400/500/600
                      // cargados como web font (ver public/index.html, que
                      // ahora agrega el peso 800).
                      fontWeight: el.negrita ? 800 : 600,
                      fontStyle: el.cursiva ? 'italic' : 'normal',
                    }}
                  >
                    {ajusteClasesDe(el) ? ajusteClasesDe(el).lineas.join('\n') : resolverTextoPreview(el, clasesEjemplo)}
                  </span>
                )}
                </div>
                {seleccionadoId === el.id && (
                  <>
                    <div className="canvas-resize-handle nw" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'nw')} />
                    <div className="canvas-resize-handle ne" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'ne')} />
                    <div className="canvas-resize-handle sw" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'sw')} />
                    <div className="canvas-resize-handle se" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'se')} />
                    {/* Bordes medios: redimensionan un solo eje (alMoverMouse ya
                        lo resuelve por esquina.includes('n'|'s'|'e'|'w')). El QR
                        no los lleva — se mantiene cuadrado solo con las esquinas. */}
                    {el.tipo !== 'qr' && (
                      <>
                        <div className="canvas-resize-handle n" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'n')} />
                        <div className="canvas-resize-handle s" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 's')} />
                        <div className="canvas-resize-handle e" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'e')} />
                        <div className="canvas-resize-handle w" onMouseDown={(e) => iniciarArrastre(e, el, 'redimensionar', 'w')} />
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <p className="form-hint canvas-ayuda">
            Arrastrá un cuadro para moverlo. Con Ctrl+clic (Cmd en Mac) sumás o sacás cuadros de la selección, Ctrl+A selecciona todos los de esta cara y Esc la limpia; con varios seleccionados cambiás de una vez color, tamaño y estilo del texto. Desde las esquinas redimensionás ancho y alto a la vez; desde los puntos del medio de cada lado, una sola dimensión. Para valores exactos usá las casillas de posición y tamaño (en milímetros sobre la tarjeta real de 85,6 × 53,98 mm) del panel de la derecha: se actualizan también cuando arrastrás.
          </p>
        </div>

        <div className="canvas-panel">
          <button type="button" className="btn-nuevo-contribuyente canvas-btn-agregar" onClick={agregarTexto}>
            <IconAgregar size={16} /> Agregar texto
          </button>

          <div className="canvas-btn-agregar-wrapper">
            <button
              type="button"
              className="btn-nuevo-contribuyente canvas-btn-agregar"
              onClick={() => setMenuImagenAbierto((v) => !v)}
              disabled={subiendoLogo}
            >
              {subiendoLogo ? (<><span className="btn-spinner"></span> Subiendo logo...</>) : (<><IconImagen size={16} /> Agregar imagen</>)}
            </button>
            {menuImagenAbierto && (
              <div className="canvas-menu-imagen">
                <button type="button" onClick={() => agregarImagen('foto_contribuyente')}>
                  <IconPersona size={16} stroke={STROKE_DEGRADE} /> Foto del contribuyente
                </button>
                {tipoTarjeta !== 'preimpresa' && (
                  <button type="button" onClick={onElegirLogoMunicipio}>
                    <IconMunicipio size={16} stroke={STROKE_DEGRADE} /> Logo del municipio
                  </button>
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              ref={logoInputRef}
              onChange={alElegirArchivoLogo}
              style={{ display: 'none' }}
            />
          </div>

          <button type="button" className="btn-nuevo-contribuyente canvas-btn-agregar" onClick={() => agregarForma('linea')}>
            <IconLinea size={16} /> Agregar línea
          </button>

          <button type="button" className="btn-nuevo-contribuyente canvas-btn-agregar" onClick={() => agregarForma('rectangulo')}>
            <IconRectangulo size={16} /> Agregar rectángulo
          </button>

          <button type="button" className="btn-nuevo-contribuyente canvas-btn-agregar" onClick={() => agregarForma('circulo')}>
            <IconCirculo size={16} /> Agregar círculo
          </button>

          <button type="button" className="btn-nuevo-contribuyente canvas-btn-agregar" onClick={() => agregarForma('qr')}>
            <IconQR size={16} /> Agregar QR
          </button>

          {seleccionMultiple ? (
            <div className="canvas-propiedades canvas-propiedades--multiple">
              <h3>{idsSeleccionados.length} elementos seleccionados</h3>
              <p className="form-hint">
                {textosSeleccionados.length} {textosSeleccionados.length === 1 ? 'texto' : 'textos'}
                {idsSeleccionados.length - textosSeleccionados.length > 0
                  ? ` · ${idsSeleccionados.length - textosSeleccionados.length} sin texto (foto, imagen, forma o QR): no se modifican`
                  : ''}.
                {' '}Color, tamaño y estilo se aplican a todos los textos seleccionados. Con las casillas de posición y tamaño fijás el mismo valor en todos los elementos (para arrastrar con el mouse, elegí uno solo).
              </p>

              <PanelPosicionTamano
                elementos={elementosSeleccionados}
                dim={dimTarjeta}
                proporcionBloqueada={proporcionBloqueada}
                onCambiarCandado={() => setProporcionBloqueada((v) => !v)}
                onAplicarMedida={aplicarMedidaASeleccion}
                onFoto34={aplicarFoto34ASeleccion}
              />

              {textosSeleccionados.length === 0 ? (
                <p className="form-hint">En esta selección no hay textos: no hay nada para cambiar en bloque.</p>
              ) : (
                <>
                  <div className="form-field">
                    <label>Tamaño de fuente</label>
                    <input
                      type="number"
                      min="6"
                      max="72"
                      className="padron-input"
                      placeholder="Mixto"
                      value={valorTamanoMulti}
                      onChange={alCambiarTamanoMulti}
                      onBlur={confirmarTamanoMulti}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                    {tamanoMulti.mixto && textoTamanoMulti === null && (
                      <span className="form-hint">Los textos tienen tamaños distintos (Mixto). Elegí uno para igualarlos.</span>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Estilo</label>
                    <div className="canvas-estilo-toggles">
                      <button
                        type="button"
                        className={`canvas-toggle-btn ${!negritaMulti.mixto && negritaMulti.valor ? 'activo' : ''} ${negritaMulti.mixto ? 'mixto' : ''}`}
                        onClick={() => alternarEstiloMulti('negrita', negritaMulti)}
                        title={negritaMulti.mixto ? 'Negrita (mixto: un clic la activa en todos)' : 'Negrita'}
                        aria-pressed={negritaMulti.mixto ? 'mixed' : !!negritaMulti.valor}
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        className={`canvas-toggle-btn ${!cursivaMulti.mixto && cursivaMulti.valor ? 'activo' : ''} ${cursivaMulti.mixto ? 'mixto' : ''}`}
                        onClick={() => alternarEstiloMulti('cursiva', cursivaMulti)}
                        title={cursivaMulti.mixto ? 'Cursiva (mixto: un clic la activa en todos)' : 'Cursiva'}
                        aria-pressed={cursivaMulti.mixto ? 'mixed' : !!cursivaMulti.valor}
                      >
                        <em>I</em>
                      </button>
                    </div>
                    {(negritaMulti.mixto || cursivaMulti.mixto) && (
                      <span className="form-hint">Un botón con borde punteado es "Mixto": los textos no coinciden.</span>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Color</label>
                    <SelectorColor
                      key={`multi-${claveSeleccion}`}
                      valor={colorMulti.mixto ? null : (colorMulti.valor || '#2c3e50')}
                      mixto={colorMulti.mixto}
                      onCambiar={(hex) => actualizarCampoTextosSeleccionados('color', hex)}
                      etiqueta="Color del texto (todos los seleccionados)"
                    />
                  </div>
                </>
              )}

              <button type="button" className="btn-cancelar" onClick={() => setSeleccionIds([])}>
                Quitar la selección (Esc)
              </button>
            </div>
          ) : elementoSeleccionado ? (
            <div className="canvas-propiedades">
              <h3>{TITULOS_PROPIEDADES[elementoSeleccionado.tipo] || 'Propiedades'}</h3>

              <PanelPosicionTamano
                elementos={[elementoSeleccionado]}
                dim={dimTarjeta}
                proporcionBloqueada={proporcionBloqueada}
                onCambiarCandado={() => setProporcionBloqueada((v) => !v)}
                onAplicarMedida={aplicarMedidaASeleccion}
                onFoto34={aplicarFoto34ASeleccion}
              />

              {elementoSeleccionado.tipo === 'texto' && (
                <>
                  <div className="form-field">
                    <label>Fuente del dato</label>
                    <select
                      className="padron-input"
                      value={elementoSeleccionado.fuente_dato}
                      onChange={(e) => actualizarCampoSeleccionado('fuente_dato', e.target.value)}
                    >
                      {FUENTES_DATO.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>

                  {elementoSeleccionado.fuente_dato === 'clases' && (() => {
                    const ajuste = ajusteClasesDe(elementoSeleccionado);
                    const aMm = (px) => `${((px * ANCHO_TARJETA_MM) / 700).toFixed(1)} mm`;
                    return (
                      <div className="form-field">
                        <label>Formato de las clases</label>
                        <select
                          className="padron-input"
                          value={formatoDeElemento(elementoSeleccionado)}
                          onChange={(e) => {
                            // "Solo códigos" es el valor por defecto: se quita la marca para que el diseño
                            // quede idéntico a como estaba antes de existir esta opción.
                            if (e.target.value === FORMATO_CODIGOS) {
                              setElementosActivos((prev) => prev.map((el) => {
                                if (el.id !== seleccionadoId) return el;
                                const { formato_clases: _quitar, ...resto } = el;
                                return resto;
                              }));
                            } else {
                              actualizarCampoSeleccionado('formato_clases', e.target.value);
                            }
                          }}
                        >
                          <option value={FORMATO_CODIGOS}>Solo códigos (ej.: B1, B2)</option>
                          <option value={FORMATO_CODIGO_DESCRIPCION}>Código + descripción, una clase por línea</option>
                        </select>
                        {ajuste && ajuste.cortado && (
                          <div className="canvas-aviso-clases canvas-aviso-clases--cortado" role="alert">
                            <strong>El texto de las clases no entra completo y se corta con "…".</strong> Aun a la
                            letra mínima de {PUNTOS_MINIMOS_IMPRESION} pt ({aMm(ajuste.fontPx)} de alto) no caben las 3
                            clases con la descripción más larga del municipio. Agrandá la caja, bajá el tamaño de letra
                            o usá "Solo códigos". Al imprimir se va a pedir confirmación.
                          </div>
                        )}
                        {ajuste && !ajuste.cortado && ajuste.reducido && (
                          <div className="canvas-aviso-clases canvas-aviso-clases--reducido">
                            El texto se achicó solo de {ajuste.baseFontPx} a {Math.round(ajuste.fontPx * 10) / 10} (mínimo
                            {' '}{PUNTOS_MINIMOS_IMPRESION} pt = {Math.round(ajuste.minimoPx * 10) / 10}) para entrar en la caja.
                          </div>
                        )}
                        {ajuste && !interDisponible() && (
                          <div className="canvas-aviso-clases canvas-aviso-clases--reducido" role="status">
                            La tipografía Inter no se cargó en este equipo (¿sin internet?). El cálculo usa una letra
                            de reemplazo y puede dar otro resultado en otros equipos.
                          </div>
                        )}
                        {ajuste && (
                          <span className="form-hint">
                            La vista previa usa el peor caso del municipio: las 3 clases activas con la descripción más
                            larga, juntas. Con menos clases o más cortas el texto entra con más holgura.
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {elementoSeleccionado.fuente_dato === 'texto_fijo' && (
                    <div className="form-field">
                      <label>Texto</label>
                      <input
                        type="text"
                        className="padron-input"
                        value={elementoSeleccionado.texto_fijo || ''}
                        onChange={(e) => actualizarCampoSeleccionado('texto_fijo', e.target.value)}
                      />
                    </div>
                  )}

                  <div className="form-field">
                    <label>Tamaño de fuente</label>
                    <input
                      type="number"
                      min="6"
                      max="72"
                      className="padron-input"
                      value={elementoSeleccionado.tamano_fuente}
                      onChange={(e) => actualizarCampoSeleccionado('tamano_fuente', e.target.value)}
                      onBlur={confirmarTamanoFuente}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>

                  <div className="form-field">
                    <label>Alineación</label>
                    <select
                      className="padron-input"
                      value={elementoSeleccionado.alineacion}
                      onChange={(e) => actualizarCampoSeleccionado('alineacion', e.target.value)}
                    >
                      <option value="izquierda">Izquierda</option>
                      <option value="centro">Centro</option>
                      <option value="derecha">Derecha</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Estilo</label>
                    <div className="canvas-estilo-toggles">
                      <button
                        type="button"
                        className={`canvas-toggle-btn ${elementoSeleccionado.negrita ? 'activo' : ''}`}
                        onClick={() => actualizarCampoSeleccionado('negrita', !elementoSeleccionado.negrita)}
                        title="Negrita"
                        aria-pressed={!!elementoSeleccionado.negrita}
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        className={`canvas-toggle-btn ${elementoSeleccionado.cursiva ? 'activo' : ''}`}
                        onClick={() => actualizarCampoSeleccionado('cursiva', !elementoSeleccionado.cursiva)}
                        title="Cursiva"
                        aria-pressed={!!elementoSeleccionado.cursiva}
                      >
                        <em>I</em>
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Color</label>
                    <SelectorColor
                      key={`${elementoSeleccionado.id}-color`}
                      valor={elementoSeleccionado.color}
                      onCambiar={(hex) => actualizarCampoSeleccionado('color', hex)}
                      etiqueta="Color"
                    />
                  </div>
                </>
              )}

              {elementoSeleccionado.tipo === 'imagen' && (
                <>
                  <div className="form-field">
                    <label>Imagen</label>
                    <select
                      className="padron-input"
                      value={elementoSeleccionado.fuente_imagen}
                      onChange={(e) => actualizarCampoSeleccionado('fuente_imagen', e.target.value)}
                    >
                      <option value="foto_contribuyente">Foto del contribuyente</option>
                      {/* En preimpresa no se ofrece logo_municipio para elementos nuevos
                          (el diseño preimpreso ya lo trae integrado) — pero si este
                          elemento YA lo tenía elegido de antes de pasar a preimpresa, se
                          deja la opción para no romper su valor actual ni obligar a
                          cambiarlo; el usuario decide si lo saca con "Eliminar elemento". */}
                      {(tipoTarjeta !== 'preimpresa' || elementoSeleccionado.fuente_imagen === 'logo_municipio') && (
                        <option value="logo_municipio">Logo del municipio</option>
                      )}
                    </select>
                  </div>

                  {/* Vía adicional a onElegirLogoMunicipio (arriba, en la barra
                      "Agregar imagen"): ese handler solo abre el picker la
                      PRIMERA vez que el diseño no tiene ningún logo todavía —
                      una vez que ya hay uno, siempre coloca el existente y
                      nunca vuelve a ofrecer elegir un archivo nuevo. Este
                      botón dispara logoInputRef incondicionalmente, así que
                      es la única forma de reemplazar un logo ya subido. */}
                  {elementoSeleccionado.fuente_imagen === 'logo_municipio' && (
                    <div className="form-field">
                      <button
                        type="button"
                        className="btn-accion btn-editar"
                        onClick={onCambiarLogo}
                        disabled={subiendoLogo}
                      >
                        {subiendoLogo ? (<><span className="btn-spinner"></span> Subiendo logo...</>) : (<><IconImagen size={14} stroke={STROKE_DEGRADE} /> Cambiar logo</>)}
                      </button>
                      <span className="form-hint">Reemplaza el logo para todos los elementos que lo usen en este diseño.</span>
                    </div>
                  )}

                  <p className="form-hint">
                    La posición y el tamaño se ajustan arrastrando el cuadro y sus esquinas en el lienzo, o escribiendo los milímetros en el recuadro de arriba.
                  </p>

                  <div className="form-field">
                    <label>Rotación (grados)</label>
                    <input
                      type="number"
                      min="-180"
                      max="180"
                      className="padron-input"
                      value={elementoSeleccionado.rotacion}
                      onChange={(e) => actualizarCampoSeleccionado('rotacion', e.target.value)}
                      onBlur={confirmarRotacion}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>
                </>
              )}

              {elementoSeleccionado.tipo === 'linea' && (
                <>
                  <div className="form-field">
                    <label>Color</label>
                    <SelectorColor
                      key={`${elementoSeleccionado.id}-color`}
                      valor={elementoSeleccionado.color}
                      onCambiar={(hex) => actualizarCampoSeleccionado('color', hex)}
                      etiqueta="Color"
                    />
                  </div>

                  <div className="form-field">
                    <label>Grosor (px)</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      className="padron-input"
                      value={elementoSeleccionado.grosor}
                      onChange={(e) => actualizarCampoSeleccionado('grosor', e.target.value)}
                      onBlur={confirmarGrosor}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>

                  <div className="form-field">
                    <label>Rotación (grados)</label>
                    <input
                      type="number"
                      min="-180"
                      max="180"
                      className="padron-input"
                      value={elementoSeleccionado.rotacion}
                      onChange={(e) => actualizarCampoSeleccionado('rotacion', e.target.value)}
                      onBlur={confirmarRotacion}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>
                </>
              )}

              {(elementoSeleccionado.tipo === 'rectangulo' || elementoSeleccionado.tipo === 'circulo') && (
                <>
                  <div className="form-field">
                    <label>Relleno</label>
                    <div className="form-field form-field-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={!elementoSeleccionado.color_relleno}
                          onChange={(e) => actualizarCampoSeleccionado('color_relleno', e.target.checked ? null : '#1a9b8e')}
                        />
                        Sin relleno
                      </label>
                    </div>
                    {elementoSeleccionado.color_relleno && (
                      <SelectorColor
                        key={`${elementoSeleccionado.id}-color_relleno`}
                        valor={elementoSeleccionado.color_relleno}
                        onCambiar={(hex) => actualizarCampoSeleccionado('color_relleno', hex)}
                        etiqueta="Color de relleno"
                      />
                    )}
                  </div>

                  <div className="form-field">
                    <label>Borde</label>
                    <div className="form-field form-field-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={!elementoSeleccionado.color_borde}
                          onChange={(e) => actualizarCampoSeleccionado('color_borde', e.target.checked ? null : '#2c3e50')}
                        />
                        Sin borde
                      </label>
                    </div>
                    {elementoSeleccionado.color_borde && (
                      <SelectorColor
                        key={`${elementoSeleccionado.id}-color_borde`}
                        valor={elementoSeleccionado.color_borde}
                        onCambiar={(hex) => actualizarCampoSeleccionado('color_borde', hex)}
                        etiqueta="Color del borde"
                      />
                    )}
                  </div>

                  {elementoSeleccionado.color_borde && (
                    <div className="form-field">
                      <label>Grosor de borde (px)</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        className="padron-input"
                        value={elementoSeleccionado.grosor_borde}
                        onChange={(e) => actualizarCampoSeleccionado('grosor_borde', e.target.value)}
                        onBlur={confirmarGrosorBorde}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    </div>
                  )}

                  {elementoSeleccionado.tipo === 'rectangulo' && (
                    <div className="form-field">
                      <label>Radio de esquina (px)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="padron-input"
                        value={elementoSeleccionado.radio_esquina}
                        onChange={(e) => actualizarCampoSeleccionado('radio_esquina', e.target.value)}
                        onBlur={confirmarRadioEsquina}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    </div>
                  )}

                  <div className="form-field">
                    <label>Rotación (grados)</label>
                    <input
                      type="number"
                      min="-180"
                      max="180"
                      className="padron-input"
                      value={elementoSeleccionado.rotacion}
                      onChange={(e) => actualizarCampoSeleccionado('rotacion', e.target.value)}
                      onBlur={confirmarRotacion}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>
                </>
              )}

              {elementoSeleccionado.tipo === 'qr' && (
                <>
                  <div className="form-field">
                    <label>Contenido</label>
                    <select
                      className="padron-input"
                      value={elementoSeleccionado.contenido_tipo}
                      onChange={(e) => actualizarCampoSeleccionado('contenido_tipo', e.target.value)}
                    >
                      {OPCIONES_CONTENIDO_QR.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <p className="form-hint">
                    Codifica DNI, nombre completo y fecha de vencimiento del contribuyente. La posición y el tamaño se ajustan arrastrando el cuadro y sus esquinas, o escribiendo los milímetros en el recuadro de arriba — siempre mantiene proporción 1:1 (cuadrado), nunca se deforma.
                  </p>

                  <div className="form-field">
                    <label>Color del QR</label>
                    <SelectorColor
                      key={`${elementoSeleccionado.id}-color_qr`}
                      valor={elementoSeleccionado.color_qr}
                      onCambiar={(hex) => actualizarCampoSeleccionado('color_qr', hex)}
                      etiqueta="Color del QR"
                    />
                  </div>

                  <div className="form-field">
                    <label>Color de fondo</label>
                    <SelectorColor
                      key={`${elementoSeleccionado.id}-color_fondo`}
                      valor={elementoSeleccionado.color_fondo}
                      onCambiar={(hex) => actualizarCampoSeleccionado('color_fondo', hex)}
                      etiqueta="Color de fondo"
                    />
                  </div>

                  {calcularContraste(elementoSeleccionado.color_qr, elementoSeleccionado.color_fondo) < UMBRAL_CONTRASTE_QR && (
                    <div className="alert alert-warning">
                      <IconAdvertencia size={18} />
                      <p>Poco contraste entre el color del QR y el fondo — puede no escanear bien.</p>
                    </div>
                  )}
                </>
              )}

              <button type="button" className="btn-eliminar-confirmar canvas-btn-eliminar" onClick={eliminarSeleccionado}>
                Eliminar elemento
              </button>
            </div>
          ) : (
            <p className="form-hint">
              Hacé click en un cuadro del lienzo para editar sus propiedades. Con Ctrl+clic (Cmd en Mac) elegís varios.
            </p>
          )}
        </div>
      </div>

      {modalImpresionAbierto && (
        <ConfiguracionImpresionModal
          valorInicial={{ tipo_impresion: tipoImpresion, tipo_tarjeta: tipoTarjeta }}
          obligatorio={false}
          onClose={() => setModalImpresionAbierto(false)}
          onSeleccionar={alElegirConfigImpresion}
        />
      )}

      {mostrarAvisoBorrador && (
        <AvisoBorradorModal onRecuperar={recuperarBorrador} onDescartar={descartarBorrador} />
      )}
    </section>
  );
};

export default DisenadorEditor;
